import { useCallback, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  FileText,
  Loader2,
  Plus,
  Save,
  Trash2,
  Copy,
  Check,
  X,
  Lock,
  CopyPlus,
} from 'lucide-react';
import { useApi } from '../api/client';
import { useCatalogs } from '../api/useCatalogs';
import type { CatalogItem, MessageTemplate } from '../types/api';

// V0 Message Bank — slice 08. Shared seeds (is_shared) are read-only;
// editing one transparently clones it server-side, so the UI just
// fires PUT and trusts the response. The seed stays visible too —
// future enhancement could let her hide seeds, but for now both
// coexist in the list.

interface ListResponse {
  message_templates: MessageTemplate[];
}

interface FormState {
  id: number | null;
  message_template_kind_id: number | '';
  name: string;
  body: string;
  notes: string;
  isFromShared: boolean; // hint to the UI: "saving will clone"
}

const EMPTY_FORM: FormState = {
  id: null,
  message_template_kind_id: '',
  name: '',
  body: '',
  notes: '',
  isFromShared: false,
};

export default function MessageTemplates() {
  const apiFetch = useApi();
  const { catalogs, reload: reloadCatalogs } = useCatalogs();
  const [templates, setTemplates] = useState<MessageTemplate[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<FormState | null>(null);

  const refetch = useCallback(async () => {
    try {
      const r = await apiFetch('/message-templates');
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d: ListResponse = await r.json();
      setTemplates(d.message_templates);
    } catch (e) {
      setError(String(e));
    }
  }, [apiFetch]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const groups = useMemo(() => {
    if (!templates || !catalogs) return [];
    const byKindId = new Map<number, MessageTemplate[]>();
    for (const t of templates) {
      const arr = byKindId.get(t.message_template_kind_id) ?? [];
      arr.push(t);
      byKindId.set(t.message_template_kind_id, arr);
    }
    return catalogs.message_template_kinds
      .filter((k) => byKindId.has(k.id))
      .map((k) => ({
        kind: k,
        items: byKindId.get(k.id) ?? [],
      }));
  }, [templates, catalogs]);

  function startNew() {
    setEditing({ ...EMPTY_FORM });
  }

  function startEdit(t: MessageTemplate) {
    setEditing({
      id: t.id,
      message_template_kind_id: t.message_template_kind_id,
      name: t.name,
      body: t.body,
      notes: '',
      isFromShared: t.is_shared,
    });
  }

  function startDuplicate(t: MessageTemplate) {
    setEditing({
      id: null,
      message_template_kind_id: t.message_template_kind_id,
      name: `${t.name} (copy)`,
      body: t.body,
      notes: '',
      isFromShared: false,
    });
  }

  async function softDelete(t: MessageTemplate) {
    if (t.is_shared) return;
    if (!confirm(`Delete "${t.name}"? This is reversible.`)) return;
    try {
      const r = await apiFetch(`/message-templates/${t.id}`, {
        method: 'DELETE',
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      await refetch();
      reloadCatalogs(); // keep /catalogs cache in sync (Quick Log picker uses it)
    } catch (e) {
      setError(String(e));
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <FileText className="h-6 w-6 text-brand-mint-600" />
        <h1 className="font-display text-3xl font-black text-brand-forest">
          Message Templates
        </h1>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-brand-sage">
          The Message Bank. Click <span className="font-bold">Copy</span> to
          paste into FB / LinkedIn / email. Editing a shared seed creates your
          own copy — the seed stays untouched.
        </p>
        <button
          type="button"
          onClick={startNew}
          className="flex items-center gap-2 rounded-xl bg-brand-lime px-4 py-2 font-display font-black text-brand-forest shadow-sm transition hover:bg-brand-lime-600"
        >
          <Plus className="h-4 w-4" strokeWidth={3} />
          New template
        </button>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {templates == null ? (
        <div className="flex items-center gap-2 text-brand-sage">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">Loading…</span>
        </div>
      ) : groups.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-brand-sage/40 bg-white p-10 text-center">
          <FileText className="mx-auto mb-3 h-8 w-8 text-brand-sage" />
          <p className="text-brand-forest">
            No templates yet. Add one with the button above.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {groups.map((g) => (
            <section key={g.kind.id} className="space-y-3">
              <h2 className="font-display text-xl font-black text-brand-forest">
                {g.kind.description}
              </h2>
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                {g.items.map((t) => (
                  <TemplateCard
                    key={t.id}
                    template={t}
                    onEdit={() => startEdit(t)}
                    onDuplicate={() => startDuplicate(t)}
                    onDelete={() => softDelete(t)}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      <EditModal
        form={editing}
        catalogs={catalogs?.message_template_kinds ?? []}
        onClose={() => setEditing(null)}
        onSaved={async () => {
          setEditing(null);
          await refetch();
          reloadCatalogs();
        }}
      />
    </div>
  );
}

interface CardProps {
  template: MessageTemplate;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

function TemplateCard({
  template,
  onEdit,
  onDuplicate,
  onDelete,
}: CardProps) {
  const [copied, setCopied] = useState(false);

  async function copyBody() {
    try {
      await navigator.clipboard.writeText(template.body);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Older browsers / non-HTTPS contexts: fall through silently.
    }
  }

  return (
    <article className="rounded-2xl border border-brand-sage/30 bg-white p-4 shadow-sm">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="font-display font-black text-brand-forest">
          {template.name}
        </h3>
        {template.is_shared ? (
          <span
            className="flex items-center gap-1 rounded-full bg-brand-paper px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-brand-sage"
            title="Shared seed; editing creates your own copy"
          >
            <Lock className="h-3 w-3" />
            Shared
          </span>
        ) : (
          <span className="rounded-full bg-brand-mint/30 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-brand-forest">
            Yours
          </span>
        )}
      </div>
      <pre className="mb-3 max-h-32 overflow-y-auto whitespace-pre-wrap rounded-md bg-brand-paper px-3 py-2 font-sans text-sm text-brand-forest/90">
        {template.body}
      </pre>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={copyBody}
          className={[
            'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-bold transition',
            copied
              ? 'bg-brand-lime-700 text-white'
              : 'bg-brand-lime text-brand-forest hover:bg-brand-lime-600',
          ].join(' ')}
        >
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
        <button
          type="button"
          onClick={onEdit}
          className="rounded-md px-3 py-1.5 text-sm font-bold text-brand-lime-700 hover:bg-brand-mint/20"
        >
          Edit
        </button>
        <button
          type="button"
          onClick={onDuplicate}
          className="flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-bold text-brand-forest hover:bg-brand-mint/20"
          title="Make a fresh copy you own"
        >
          <CopyPlus className="h-3.5 w-3.5" />
          Duplicate
        </button>
        {!template.is_shared && (
          <button
            type="button"
            onClick={onDelete}
            className="ml-auto flex items-center gap-1 rounded-md px-3 py-1.5 text-sm text-red-600 hover:bg-red-50"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Delete
          </button>
        )}
      </div>
    </article>
  );
}

interface EditModalProps {
  form: FormState | null;
  catalogs: CatalogItem[];
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

function EditModal({ form, catalogs, onClose, onSaved }: EditModalProps) {
  const apiFetch = useApi();
  const [draft, setDraft] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (form) {
      setDraft(form);
      setError(null);
    }
  }, [form]);

  if (!form) return null;
  const isNew = form.id == null;
  const willClone = !isNew && form.isFromShared;

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setDraft((d) => ({ ...d, [key]: value }));
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (saving) return;
    if (!draft.name.trim() || !draft.body.trim() || draft.message_template_kind_id === '') {
      setError('Kind, name, and body are required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        message_template_kind_id: draft.message_template_kind_id,
        name: draft.name.trim(),
        body: draft.body,
        notes: draft.notes.trim() || null,
      };
      const url = isNew
        ? '/message-templates'
        : `/message-templates/${draft.id}`;
      const r = await apiFetch(url, {
        method: isNew ? 'POST' : 'PUT',
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      await onSaved();
    } catch (err) {
      setError(String(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-brand-forest-900/60 px-4 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.92, y: 10, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 320, damping: 26 }}
          className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between rounded-t-2xl bg-gradient-to-r from-brand-mint to-brand-sky px-5 py-3 text-brand-forest">
            <div className="font-display font-black">
              {isNew ? 'New template' : draft.name || 'Edit template'}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1 transition hover:bg-brand-forest/10"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <form onSubmit={save} className="space-y-4 px-5 py-5">
            {willClone && (
              <div className="rounded-md border border-brand-sky/40 bg-brand-sky/10 px-3 py-2 text-sm text-brand-forest">
                Saving this will create your own copy. The shared seed
                stays unchanged.
              </div>
            )}

            <Field label="Kind">
              <select
                value={draft.message_template_kind_id}
                onChange={(e) =>
                  setField(
                    'message_template_kind_id',
                    e.target.value === '' ? '' : Number(e.target.value),
                  )
                }
                className="w-full rounded-md border border-brand-sage/40 bg-white px-3 py-2 text-brand-forest focus:border-brand-lime focus:outline-none focus:ring-2 focus:ring-brand-lime/30"
              >
                <option value="">—</option>
                {catalogs.map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.description}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Name">
              <input
                type="text"
                value={draft.name}
                onChange={(e) => setField('name', e.target.value)}
                placeholder="e.g. Cold reach-out"
                required
                className="w-full rounded-md border border-brand-sage/40 bg-white px-3 py-2 text-brand-forest focus:border-brand-lime focus:outline-none focus:ring-2 focus:ring-brand-lime/30"
              />
            </Field>

            <Field label="Body">
              <textarea
                value={draft.body}
                onChange={(e) => setField('body', e.target.value)}
                rows={10}
                required
                placeholder="Hey [Name], …"
                className="w-full resize-y rounded-md border border-brand-sage/40 bg-white px-3 py-2 font-sans text-sm text-brand-forest focus:border-brand-lime focus:outline-none focus:ring-2 focus:ring-brand-lime/30"
              />
              <p className="mt-1 text-xs text-brand-sage">
                Use <code>[Name]</code> as a placeholder — replace before sending.
              </p>
            </Field>

            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}

            <div className="flex items-center justify-end gap-2 border-t border-brand-sage/20 pt-3">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md px-4 py-2 text-sm text-brand-forest hover:bg-brand-sage/10"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex items-center gap-2 rounded-xl bg-brand-lime px-4 py-2 font-display font-black text-brand-forest shadow-sm transition hover:bg-brand-lime-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {saving ? 'Saving…' : willClone ? 'Save my copy' : 'Save'}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-brand-sage">
        {label}
      </label>
      {children}
    </div>
  );
}
